'use client';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  OutlinedInput,
  CircularProgress,
  Typography,
  Stack,
  LinearProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DataTable from '../../components/DataTable';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import { filterReportFuelSummary, getAllSensorReport } from './constant';
import { timezone } from '../../utils/timeZone';
import { useLanguage } from '../../utils/i18n';
import { API_BASES } from '../../config/constants';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useNotifier } from '../../components/Notifier';
import { useCompanyDataList } from '../../stores/companyStore';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

function VehicleFuelHistoryPageContent() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [rows, setRows] = useState([]);

  const [tableSearchQuery, setTableSearchQuery] = useState('');

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
      to = now.second(59);
    } else if (periodType === 'week' || periodType === 'last_week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month' || periodType === 'last_month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });
  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('vehicleFuelHistory.errMaxRange31Days', 'Maksimal rentang waktu 31 hari'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('vehicleFuelHistory.errStartTimeLater', 'Waktu awal tidak boleh melebihi waktu akhir'));
      return false;
    }
    setDateError('');
    return true;
  };

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          'Unknown Company',
        raw: c.raw ?? c
      })),
    [vehicleList]
  );

  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => {
    return selectedVehicles.map((v) => String(v.value));
  }, [selectedVehicles]);

  const handleVehicleChange = (ids) => {
    const newSelected = ids.map((id) => {
      const strId = String(id);
      const match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
      if (match) return match;
      const prevMatch = selectedVehicles.find((v) => String(v.value) === strId);
      if (prevMatch) return prevMatch;

      const rawMatch =
        (vehicleList || []).find((v) => String(v.value) === strId) ||
        (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
      if (rawMatch) {
        return {
          value: strId,
          label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
          companyName: rawMatch.companyName,
          raw: rawMatch.raw
        };
      }
      return { value: strId, label: strId };
    });
    setSelectedVehicles(newSelected);
  };

  const fetchData = useCallback(async () => {
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('vehicleFuelHistory.errSelectVehicle', 'Select at least one vehicle'), severity: 'warning' });
      return;
    }
    if (!fromDate || !toDate) {
      notify({ message: t('vehicleFuelHistory.errSelectPeriod', 'Please select a date period'), severity: 'warning' });
      return;
    }
    if (dayjs(fromDate).isAfter(dayjs(toDate))) {
      notify({
        message: t('vehicleFuelHistory.errFromLaterThanTo', 'The "From" date cannot be greater than the "To" date.'),
        severity: 'error'
      });
      return;
    }
    setLoading(true);
    setLoadingProgress(0);
    setLoadingText('Preparing data...');

    try {
      let targetIds = [];
      const isSelectAll = selectedVehicleIds.length === 0 || selectedVehicleIds.length === vehicleOptionsNormalized.length;

      if (isSelectAll) {
        targetIds = vehicleOptionsNormalized.map((v) => String(v.value));
      } else {
        targetIds = selectedVehicleIds;
      }
      if (targetIds.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      let apiChunks = [];
      const localStart = dayjs(fromDate).second(0);
      const localEnd = dayjs(toDate).second(59);
      const pType = periodType;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          apiChunks.push({
            startLocal: curr.startOf('day'),
            endLocal: curr.endOf('day'),
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            startLocal: chunkStart,
            endLocal: chunkEnd,
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });

          curr = curr.add(1, 'day');
        }
      }

      const allNestedResults = [];
      const totalVehicles = targetIds.length;
      let completedVehicles = 0;
      const chunkSize = 5;

      for (let i = 0; i < totalVehicles; i += chunkSize) {
        const chunk = targetIds.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (id) => {
            const vInfo =
              selectedVehicles.find((v) => String(v.value) === String(id)) ||
              vehicleOptionsNormalized.find((v) => String(v.value) === String(id));
            const vCompanyId = vInfo?.raw?.company || vInfo?.raw?.join?.company_code || 0;
            const licensePlate = vInfo?.label || String(id);

            let vehicleDailyData = [];

            const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
            for (const chunk of apiChunks) {
              const apiBeginTime = chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
              const apiEndTime = chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

              const payload = {
                company_id: Number(vCompanyId),
                start_date: apiBeginTime,
                end_date: apiEndTime,
                vehicle_id: Number(id),
                tz
              };

              try {
                const fuelPromise = filterReportFuelSummary(payload, tz);
                const sensorPromise = getAllSensorReport(payload);
                const [fuelRes, sensorRes] = await Promise.all([fuelPromise, sensorPromise]);

                let fuelDataArray = [];
                if (Array.isArray(fuelRes)) {
                  fuelDataArray = fuelRes;
                } else if (fuelRes?.data && Array.isArray(fuelRes.data)) {
                  fuelDataArray = fuelRes.data;
                } else if (fuelRes?.data && typeof fuelRes.data === 'object') {
                  fuelDataArray = [fuelRes.data];
                } else {
                  fuelDataArray = [];
                }

                const sensorData = sensorRes?.data || {};
                const avgSpeed = sensorData.avg_speed || 0;

                if (fuelDataArray.length > 0) {
                  const mapped = fuelDataArray.map((row) => ({
                    ...row,
                    avg_speed: avgSpeed,
                    id: row.id ?? `${row.vehicle_id}_${Math.random()}`
                  }));
                  vehicleDailyData = vehicleDailyData.concat(mapped);
                }
              } catch (err) {
                console.error(`Error fetching for ${licensePlate} on ${apiBeginTime}`, err);
              }
            }

            completedVehicles++;
            setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
            setLoadingText(`Processing unit: ${licensePlate} (${completedVehicles}/${totalVehicles})`);
            return vehicleDailyData;
          })
        );
        allNestedResults.push(...chunkResults.flat());
      }

      const rawData = allNestedResults;

      setLoadingText(`Processing alamat lokasi...`);
      setLoadingProgress(0);

      const dataWithAddresses = [];
      const geocodeChunkSize = 5;
      for (let i = 0; i < rawData.length; i += geocodeChunkSize) {
        const chunk = rawData.slice(i, i + geocodeChunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (row) => {
            let addressFound = '-';
            const lat = row.last_lat;
            const lon = row.last_lon;
            if (lat && lon) {
              try {
                const geoRes = await fetch(
                  `${API_BASES.ADDRESS}/reverse.php?lat=${lat}&lon=${lon}&zoom=18&format=json&addressdetails=1&limit=1`
                );
                const geoJson = await geoRes.json();
                if (geoJson && geoJson.code === 200 && geoJson.data && geoJson.data.display_name) {
                  addressFound = geoJson.data.display_name;
                }
              } catch (err) {
                console.error(`Gagal fetch alamat untuk ${row.license_plate}:`, err);
              }
            }
            return {
              ...row,
              fetched_address: addressFound,
              id: row.id ?? `${row.vehicle_id}_${Math.random()}`
            };
          })
        );
        dataWithAddresses.push(...chunkResults);
        setLoadingProgress(Math.round(((i + chunk.length) / rawData.length) * 100));
      }
      setRows(dataWithAddresses);
    } catch (error) {
      console.error('Gagal mengambil data report:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicleIds, fromDate, toDate, notify, t, vehicleOptionsNormalized, selectedVehicles]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (tableSearchQuery) {
      const lowerQ = tableSearchQuery.toLowerCase();
      result = result.filter(
        (row) =>
          (row.plate || row.license_plate || '').toLowerCase().includes(lowerQ) ||
          (row.type || row.vehicle_type_name || '').toLowerCase().includes(lowerQ)
      );
    }
    return result;
  }, [rows, tableSearchQuery]);

  const columns = useMemo(
    () => [
      { field: 'license_plate', headerName: t('vehicleFuelHistory.plateNumber', 'Plat Nomor'), width: 130 },
      { field: 'vehicle_type_name', headerName: t('vehicleFuelHistory.vehicleType', 'Jenis Kendaraan'), width: 150 },
      {
        field: 'total_bbm_usage',
        headerName: t('vehicleFuelHistory.fuelConsumption', 'Fuel Consumption (L)'),
        width: 180,
        valueGetter: (value, params) => {
          const row = params || {};
          const usage = Number(row.total_bbm_usage).toFixed(2) || 0;
          const usageCanbus = Number(row.total_bbm_usage_canbus).toFixed(2) || 0;
          if (usage <= 0) {
            return usageCanbus;
          } else {
            return usage;
          }
        }
      },
      {
        field: 'speed',
        headerName: t('vehicleFuelHistory.avgSpeed', 'Kecepatan Rata-rata (Km/H)'),
        width: 180,
        valueGetter: (value, params) => {
          return params.avg_speed.toFixed(2) || 0;
        }
      },
      {
        field: 'location',
        headerName: t('vehicleFuelHistory.location', 'Lokasi'),
        width: 450,
        valueGetter: (value, row) => {
          return row?.fetched_address || '-';
        },
        renderCell: (params) => {
          const row = params.row || {};
          const address = row.fetched_address || '-';
          const lat = row.last_lat;
          const lon = row.last_lon;
          if (lat && lon) {
            return (
              <a
                href={`http://maps.google.com/maps?z=18&q=${lat},${lon}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', color: '#1976d2' }}
              >
                {address}
              </a>
            );
          }
          return address;
        }
      }
    ],
    [t]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 0.5,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
          }}
        >
          <Box
            sx={{
              p: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2
            }}
          >
            <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
              {t('reports.fuelHistoryDesc', 'Fuel decrease or increase with speed info, ACC on/off, and location.')}
            </Typography>
            {(localStorage.getItem('rolename') === 'Administrator' ||
              (() => {
                try {
                  return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes(
                    '"modules_path":"report-catalog"'
                  );
                } catch {
                  return false;
                }
              })()) && (
              <Box>
                <Button
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  onClick={() => navigate('/report-catalog?name=report-catalog')}
                  size="small"
                  variant="contained"
                  sx={{
                    bgcolor: 'primary',
                    color: '#ffffff',
                    textTransform: 'none',
                    px: 0.5,
                    py: 0,
                    minWidth: 0,
                    '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                  }}
                >
                  Report Catalog
                </Button>
              </Box>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label="Company"
                value={selectedCompanyIds}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicles([]);
                }}
                dialogTitle="Select Company"
                multiSelect={true}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <PickerField
                label={t('general.vehicle', 'Select Vehicle')}
                value={selectedVehicleIds}
                selectedItems={selectedVehicles}
                {...vehiclePickerParams}
                options={vehicleOptionsNormalized}
                onChange={handleVehicleChange}
                dialogTitle={t('general.vehicle', 'Select Vehicle')}
                multiSelect={true}
              />
            </Box>
            <Box sx={{ flex: '0 1 180px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="period-label">{t('vehicleFuelHistory.selectPeriod', 'Pilih Periode')}</InputLabel>
                <MuiSelect
                  labelId="period-label"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  disabled={loading}
                  input={<OutlinedInput label={t('vehicleFuelHistory.selectPeriod', 'Pilih Periode')} />}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">{t('vehicleFuelHistory.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="month">{t('vehicleFuelHistory.lastMonth', 'Last Month')}</MenuItem>
                  <MenuItem value="custom">{t('vehicleFuelHistory.custom', 'Custom')}</MenuItem>
                </MuiSelect>
              </FormControl>
            </Box>

            {periodType === 'custom' && (
              <Box sx={{ flex: '1 1 350px' }}>
                <Stack direction="row" spacing={2}>
                  <DateTimePicker
                    ampm={false}
                    label={t('vehicleFuelHistory.from', 'From')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(0).format('YYYY-MM-DD HH:mm:ss') : '';
                      setFromDate(newV);
                      validateDateRange(dayjs(newV), dayjs(toDate));
                    }}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                  <DateTimePicker
                    ampm={false}
                    label={t('vehicleFuelHistory.to', 'To')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(59).format('YYYY-MM-DD HH:mm:ss') : '';
                      setToDate(newV);
                      validateDateRange(dayjs(fromDate), dayjs(newV));
                    }}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                </Stack>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', mt: { xs: 2, md: 0 } }}>
              <Button
                variant="outlined"
                color="inherit"
                disabled={loading}
                onClick={() => {
                  setSelectedCompanyIds(() => {
                    const defaultId = getCompanyIdFromStorage();
                    return defaultId ? [defaultId] : [];
                  });
                  setSelectedVehicles([]);
                  setTableSearchQuery('');
                  setRows([]);
                  setPeriodType('today');
                  setDateError('');
                }}
                sx={{ px: 2, minWidth: 0, bgcolor: '#ffffff' }}
              >
                <RestartAltIcon />
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={fetchData}
                disabled={loading || !!dateError || selectedVehicleIds.length === 0}
                sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
              >
                {loading ? t('buttonTitle.generating', 'Generating...') : t('buttonTitle.generate', 'Generate')}
              </Button>
            </Box>
          </Box>
          {dateError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {dateError}
            </Typography>
          )}
        </Paper>

        {rows.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mt: 0.5,
              mb: 0.5,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              bgcolor: 'rgba(72, 93, 154, 0.05)',
              border: '1px solid rgba(72, 93, 154, 0.2)',
              boxShadow: '0 2px 8px rgba(72, 93, 154, 0.05)'
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
              {t('revenue.showingDataFrom', 'Showing data from')}{' '}
              <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
              </Box>{' '}
              {t('revenue.to', 'to')}{' '}
              <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
              </Box>
            </Typography>
          </Box>
        )}

        {loading && loadingProgress > 0 && (
          <Box
            sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, mb: 2, border: '1px dashed rgba(72, 93, 154, 0.3)', mt: 2 }}
          >
            <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1.5,
                  bgcolor: 'rgba(72, 93, 154, 0.2)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                }}
              />
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                {loadingProgress}% - {loadingText}
              </Typography>
            </Box>
          </Box>
        )}

        <DataTable
          title="Vehicle Fuel History"
          columns={columns}
          rows={filteredRows}
          loading={loading}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          searchQuery={tableSearchQuery}
          onSearchChange={(e) => setTableSearchQuery(e.target.value)}
          searchLabel={t('vehicleFuelHistory.searchLicensePlate', 'Search License Plate')}
          hideAddButton={true}
        />
      </Box>
    </LocalizationProvider>
  );
}
export default function VehicleFuelHistoryPage() {
  return (
    <>
      <VehicleFuelHistoryPageContent />
    </>
  );
}
